(() => {
  if (window.RolifeCollectionUI) {
    window.RolifeCollectionUI.initAll(document);
    return;
  }

  const controllers = new WeakMap();

  function initRoot(root) {
    if (!(root instanceof HTMLElement) || controllers.has(root)) return;

    const controller = new AbortController();
    const filterToggle = root.querySelector("[data-rolife-filter-toggle]");
    const filters = root.querySelector("[data-rolife-filters]");
    const testimonialRoot = root.querySelector("[data-rolife-testimonials]");
    const testimonialTrack = testimonialRoot?.querySelector(
      "[data-rolife-testimonial-track]",
    );
    const testimonialSlides = [
      ...(testimonialRoot?.querySelectorAll("[data-rolife-testimonial]") ?? []),
    ];
    const testimonialCounter = testimonialRoot?.querySelector(
      "[data-rolife-testimonial-counter]",
    );
    const testimonialPrevious = testimonialRoot?.querySelector(
      "[data-rolife-testimonial-prev]",
    );
    const testimonialNext = testimonialRoot?.querySelector(
      "[data-rolife-testimonial-next]",
    );

    filterToggle?.addEventListener(
      "click",
      () => {
        const expanded =
          filterToggle.getAttribute("aria-expanded") === "true";
        filterToggle.setAttribute("aria-expanded", String(!expanded));
        filters?.classList.toggle("is-open", !expanded);
      },
      { signal: controller.signal },
    );

    function testimonialMetrics() {
      const firstSlide = testimonialSlides[0];
      if (!testimonialTrack || !firstSlide) {
        return { page: 0, pages: 0, step: 0 };
      }
      const styles = getComputedStyle(testimonialTrack);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const visible = Math.max(
        1,
        Math.round((testimonialTrack.clientWidth + gap) / (slideWidth + gap)),
      );
      const pages = Math.ceil(testimonialSlides.length / visible);
      const step = (slideWidth + gap) * visible;
      const page = Math.min(
        pages - 1,
        Math.max(0, Math.round(testimonialTrack.scrollLeft / step)),
      );
      return { page, pages, step };
    }

    function syncTestimonialCounter() {
      const { page, pages } = testimonialMetrics();
      if (testimonialCounter) {
        testimonialCounter.textContent = pages > 0 ? `${page + 1} / ${pages}` : "0 / 0";
      }
      if (testimonialPrevious) testimonialPrevious.disabled = page <= 0;
      if (testimonialNext) testimonialNext.disabled = page >= pages - 1;
    }

    function moveTestimonials(direction) {
      const { page, pages, step } = testimonialMetrics();
      if (!testimonialTrack || pages <= 1 || step <= 0) return;
      const destination = Math.min(pages - 1, Math.max(0, page + direction));
      testimonialTrack.scrollTo({ left: destination * step, behavior: "smooth" });
    }

    testimonialPrevious?.addEventListener(
      "click",
      () => moveTestimonials(-1),
      { signal: controller.signal },
    );
    testimonialNext?.addEventListener(
      "click",
      () => moveTestimonials(1),
      { signal: controller.signal },
    );
    testimonialTrack?.addEventListener("scrollend", syncTestimonialCounter, {
      signal: controller.signal,
    });
    window.addEventListener("resize", syncTestimonialCounter, {
      signal: controller.signal,
    });
    syncTestimonialCounter();

    controllers.set(root, controller);
    root.setAttribute("data-rolife-collection-initialized", "true");
  }

  function teardownRoot(root) {
    const controller = controllers.get(root);
    controller?.abort();
    controllers.delete(root);
    root?.removeAttribute("data-rolife-collection-initialized");
  }

  function initAll(scope = document) {
    if (scope.matches?.("[data-rolife-collection]")) initRoot(scope);
    for (const root of scope.querySelectorAll?.(
      "[data-rolife-collection]",
    ) ?? []) {
      initRoot(root);
    }
  }

  function teardownAll(scope = document) {
    if (scope.matches?.("[data-rolife-collection]")) teardownRoot(scope);
    for (const root of scope.querySelectorAll?.(
      "[data-rolife-collection]",
    ) ?? []) {
      teardownRoot(root);
    }
  }

  const api = { initAll, teardownAll };
  window.RolifeCollectionUI = api;

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    teardownAll(event.target);
  });
  initAll(document);
})();
